import React, { useEffect, useRef } from 'react';
import './OracleAnimation.css';

const OracleAnimation = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Animation variables
    let time = 0;
    const particles = [];
    const numParticles = 150;
    
    // OrAi eye properties (positioned at center of void)
    const eye = {
      x: 0,
      y: 0,
      lookX: 0,
      lookY: 0,
      targetLookX: 0,
      targetLookY: 0
    };

    // Mouse tracking
    const mouse = { x: 0, y: 0 };
    
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    
    // Track mouse on entire document, not just canvas
    document.addEventListener('mousemove', handleMouseMove);

    // Create particles for void effect
    for (let i = 0; i < numParticles; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.8 + 0.2,
        originalSize: Math.random() * 3 + 1
      });
    }

    const animate = () => {
      time += 0.01;
      
      // Clear canvas with subtle fade effect
      ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      // Update eye position
      eye.x = centerX;
      eye.y = centerY;
      
      // Calculate eye look direction based on mouse position
      const dx = mouse.x - centerX;
      const dy = mouse.y - centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const maxLookDistance = 8; // Reduced for more subtle movement
      
      if (distance > 0) {
        eye.targetLookX = (dx / distance) * Math.min(distance / 100, 1) * maxLookDistance;
        eye.targetLookY = (dy / distance) * Math.min(distance / 100, 1) * maxLookDistance;
      }
      
      // Smooth eye movement
      eye.lookX += (eye.targetLookX - eye.lookX) * 0.1;
      eye.lookY += (eye.targetLookY - eye.lookY) * 0.1;

      // Draw void event horizon (black hole center)
      const voidGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 120);
      voidGradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
      voidGradient.addColorStop(0.3, 'rgba(0, 0, 0, 0.9)');
      voidGradient.addColorStop(0.6, 'rgba(30, 30, 30, 0.6)');
      voidGradient.addColorStop(0.8, 'rgba(60, 60, 60, 0.3)');
      voidGradient.addColorStop(1, 'rgba(100, 100, 100, 0.1)');
      
      ctx.fillStyle = voidGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 120, 0, Math.PI * 2);
      ctx.fill();

      // Draw rotating accretion disk
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(time * 0.3);
      
      for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2;
        const radius = 140 + Math.sin(time * 2 + i) * 15;
        
        ctx.beginPath();
        ctx.strokeStyle = `rgba(${80 + i * 15}, ${80 + i * 15}, ${80 + i * 15}, ${0.4 - i * 0.05})`;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 10]);
        ctx.arc(0, 0, radius, angle, angle + Math.PI / 3);
        ctx.stroke();
      }
      
      ctx.setLineDash([]);
      ctx.restore();

      // Update and draw void particles
      particles.forEach(particle => {
        // Calculate distance to center
        const dx = centerX - particle.x;
        const dy = centerY - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Apply gravitational pull toward void
        if (distance > 40) {
          const force = 150 / (distance * distance);
          particle.vx += (dx / distance) * force;
          particle.vy += (dy / distance) * force;
          
          // Add orbital motion
          particle.vx += (-dy / distance) * force * 0.4;
          particle.vy += (dx / distance) * force * 0.4;
        }
        
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;
        
        // Apply friction
        particle.vx *= 0.99;
        particle.vy *= 0.99;
        
        // Reset particles that get too close to center or leave canvas
        if (distance < 40 || particle.x < 0 || particle.x > canvas.width || 
            particle.y < 0 || particle.y > canvas.height) {
          particle.x = Math.random() * canvas.width;
          particle.y = Math.random() * canvas.height;
          particle.vx = (Math.random() - 0.5) * 2;
          particle.vy = (Math.random() - 0.5) * 2;
        }
        
        // Draw particle with distance-based opacity
        ctx.save();
        ctx.globalAlpha = particle.opacity * (1 - distance / 400);
        const intensity = Math.floor(200 - distance * 0.4);
        ctx.fillStyle = `rgba(${intensity}, ${intensity}, ${intensity}, 1)`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

              // Draw the OrAi eye at the center of the void
      ctx.save();
      ctx.translate(centerX + eye.lookX, centerY + eye.lookY);
      
      // Eye shape (narrowed, angry ellipse instead of circle)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.beginPath();
      ctx.ellipse(0, 0, 35, 20, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Eye outer border (thick, highly visible)
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.ellipse(0, 0, 35, 20, 0, 0, Math.PI * 2);
      ctx.stroke();
      
      // Eye iris (bright red for anger)
      ctx.fillStyle = 'rgba(180, 20, 20, 0.9)';
      ctx.beginPath();
      ctx.arc(0, 0, 18, 0, Math.PI * 2);
      ctx.fill();
      
      // Pupil (pure void)
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      ctx.arc(0, 0, 10, 0, Math.PI * 2);
      ctx.fill();
      
      // Single bright highlight
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.beginPath();
      ctx.arc(-6, -6, 3, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.restore();

      // Draw gravitational lensing effect around the void
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const lensGradient = ctx.createRadialGradient(centerX, centerY, 110, centerX, centerY, 130);
      lensGradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
      lensGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.05)');
      lensGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      
      ctx.fillStyle = lensGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 130, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();



      requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div className="oracle-animation-container">
      <canvas ref={canvasRef} className="oracle-canvas" />
    </div>
  );
};

export default OracleAnimation; 